const RELOAD_GUARD_PREFIX = "stale-chunk-reload:";
const RELOAD_GUARD_WINDOW_MS = 60_000;

// The path a route navigation is heading to, recorded by a router guard before
// the route's lazy chunk loads (see router/index.ts). When a route chunk is
// what fails, recovery reloads to THIS path so the user lands on the page they
// clicked — not the one they started on, which reloading in place would do and
// which reads as "my click did nothing, I have to click again". null when no
// navigation is in flight, where a failing widget / defineAsyncComponent import
// on the current page should just reload in place.
let pendingNavigationPath: string | null = null;

export function recordNavigationTarget(path: string | null): void {
  pendingNavigationPath = path;
}

/**
 * Self-heal tabs whose content-hashed chunks were replaced by a deploy.
 *
 * Vite dispatches `vite:preloadError` for ANY failed dynamic-import or CSS
 * preload — route-level lazy chunks, widget `import()`s, and
 * `defineAsyncComponent` wrappers alike — so one listener covers what a
 * router-only handler cannot, without depending on browser-specific error
 * message wording. One reload picks up a fresh index.html and with it fresh
 * chunk URLs.
 *
 * We reload to the in-flight route target rather than the current URL: a route
 * chunk fails before the router commits the navigation, so the location is
 * still the old page — reloading it would strand the user where they were and
 * silently swallow the click. Falling back to the current path covers non-route
 * imports, where there is no pending navigation to preserve.
 *
 * Guards:
 * - Offline failures are transient — a hard reload would land on the browser
 *   error page and lose in-memory SPA state, where waiting for reconnect
 *   recovers for free.
 * - At most one reload per target per minute: a genuinely broken chunk still
 *   surfaces (the un-prevented event throws and reaches Sentry), while a
 *   LATER deploy hitting the same long-lived tab re-arms naturally. Keying on
 *   the target (not the current page) means navigating between two broken
 *   sections isn't blocked by a single shared guard.
 */
export function installStaleChunkReload(): void {
  window.addEventListener("vite:preloadError", (event: Event) => {
    if (!navigator.onLine) {
      return;
    }
    const target = pendingNavigationPath ?? window.location.pathname;
    const key = RELOAD_GUARD_PREFIX + target;
    const lastReload = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - lastReload < RELOAD_GUARD_WINDOW_MS) {
      return;
    }
    event.preventDefault();
    sessionStorage.setItem(key, String(Date.now()));
    window.location.assign(target);
  });
}
